import { useEffect } from 'react'
import { Link } from 'react-router'
import Header from '../sections/Header'
import Footer from '../sections/Footer'

export default function PrivacyPage() {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="relative">
      <Header />
      <main className="pt-24 pb-20 bg-[#0d1117] min-h-screen">
        <div className="max-w-[720px] mx-auto px-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-[#06b6d4] hover:text-[#22d3ee] transition-colors mb-8"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Home
          </Link>

          <h1 className="text-3xl font-semibold text-white mb-2">Privacy Policy</h1>
          <p className="text-sm text-[#6b7280] font-mono mb-12">Last Updated: June 14, 2026</p>

          <div className="space-y-10 text-[15px] text-[#d1d5db] leading-relaxed">
            <section>
              <h2 className="text-lg font-semibold text-white mb-3">1. Introduction</h2>
              <p>
                ProcurAlerts ("we," "us," or "our") operates the procuralerts.com website and platform.
                This Privacy Policy explains how we collect, use, and protect your information when you
                use our Service.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">2. Information We Collect</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-white font-medium mb-1">Account Information</h3>
                  <p>When you register, we collect your name, email address, company name, and service area preferences (states, counties, and trade categories).</p>
                </div>
                <div>
                  <h3 className="text-white font-medium mb-1">Billing Information</h3>
                  <p>Payment processing is handled by Stripe. We do not store your credit card number, CVV, or full payment details on our servers. Stripe's privacy policy governs payment data handling.</p>
                </div>
                <div>
                  <h3 className="text-white font-medium mb-1">Usage Data</h3>
                  <p>We collect information about how you interact with the Service, including pages visited, bids viewed, watchlist activity, and feature usage. This helps us improve the product.</p>
                </div>
                <div>
                  <h3 className="text-white font-medium mb-1">Document Uploads</h3>
                  <p>The Document Vault stores files such as insurance certificates and licenses, associated with your account. Document uploads are available on paid plans; uploads are disabled during the free trial.</p>
                </div>
                <div>
                  <h3 className="text-white font-medium mb-1">Communications</h3>
                  <p>If you contact us or use the Request Info feature, we retain the content of those communications.</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">3. How We Use Your Information</h2>
              <ul className="space-y-2 list-disc list-inside marker:text-[#6b7280]">
                <li>To provide and maintain the Service, including delivering matched bid alerts.</li>
                <li>To process your subscription and billing.</li>
                <li>To send transactional emails (bid alerts, account notifications, digest emails).</li>
                <li>To improve the Service based on usage patterns.</li>
                <li>To respond to your inquiries and support requests.</li>
              </ul>
              <p className="mt-4 font-medium text-white">We do not sell, rent, or trade your personal information to third parties.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">4. Email Communications</h2>
              <p className="mb-3">We send the following types of emails:</p>
              <ul className="space-y-2 list-disc list-inside marker:text-[#6b7280]">
                <li>Bid alert notifications based on your configured preferences.</li>
                <li>Daily or weekly digest emails summarizing new opportunities.</li>
                <li>Account-related emails (password resets, subscription confirmations, team invitations).</li>
              </ul>
              <p className="mt-3">
                You can manage your email preferences in your Profile settings. Transactional emails
                related to your account cannot be opted out of while your account is active.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">5. Data Sharing</h2>
              <p className="mb-3">We share your information only in the following circumstances:</p>
              <ul className="space-y-2 list-disc list-inside marker:text-[#6b7280]">
                <li>With Stripe for payment processing.</li>
                <li>With our email delivery provider (currently Brevo) to send transactional and digest emails.</li>
                <li>If required by law, regulation, or legal process.</li>
                <li>To protect the rights, safety, or property of ProcurAlerts or its users.</li>
              </ul>
              <p className="mt-4 font-medium text-white">We do not share your data with advertisers or data brokers.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">6. Data Security</h2>
              <p>
                We implement reasonable technical and organizational measures to protect your data,
                including encrypted connections (HTTPS), secure password hashing, and access controls.
                However, no method of electronic transmission or storage is 100% secure.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">7. Data Retention</h2>
              <p className="mb-3">
                We retain your account data for as long as your account is active. If you delete your
                account, we remove your personal data, except where retention is required by law or
                for legitimate business purposes (e.g., billing records).
              </p>
              <p>
                You can delete uploaded documents at any time. When an account is deleted, documents
                associated with the account are removed along with other account data.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">8. Your Rights</h2>
              <p className="mb-3">You may:</p>
              <ul className="space-y-2 list-disc list-inside marker:text-[#6b7280]">
                <li>Access and update your personal information through your Profile.</li>
                <li>Delete your account and associated data.</li>
                <li>Request a copy of the personal data we hold about you by contacting us.</li>
                <li>Opt out of non-essential email communications.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">9. Cookies</h2>
              <p>
                ProcurAlerts uses cookies that are required to operate the Service — for example, to
                keep you signed in. We do not use third-party advertising or cross-site tracking
                cookies.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">10. Children's Privacy</h2>
              <p>
                ProcurAlerts is not intended for users under 18. We do not knowingly collect information
                from children.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">11. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. Changes will be posted on this page
                with an updated "Last Updated" date. Continued use of the Service after changes
                constitutes acceptance.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-white mb-3">12. Contact</h2>
              <p>
                For privacy-related questions, contact us at{' '}
                <a href="mailto:hello@procuralerts.com" className="text-[#06b6d4] hover:text-[#22d3ee] transition-colors">
                  hello@procuralerts.com
                </a>.
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}
